import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Target, DollarSign, CalendarRange } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Container, Eyebrow, Reveal, GlowOrb } from "@/components/kit";

const fmt = (n) =>
  n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${Math.round(n)}`;
const fmtFull = (n) => `$${Math.round(n).toLocaleString()}`;

export const RoiEstimator = () => {
  const [meetings, setMeetings] = useState(30);
  const [closeRate, setCloseRate] = useState(22);
  const [acv, setAcv] = useState(18000);

  const { deals, monthlyRev, annualRev, annualDeals } = useMemo(() => {
    const d = meetings * (closeRate / 100);
    return {
      deals: d,
      monthlyRev: d * acv,
      annualDeals: d * 12,
      annualRev: d * acv * 12,
    };
  }, [meetings, closeRate, acv]);

  const controls = [
    {
      label: "Qualified meetings / month",
      value: meetings,
      set: setMeetings,
      min: 5,
      max: 100,
      step: 1,
      display: `${meetings}`,
      icon: CalendarRange,
    },
    {
      label: "Meeting-to-close rate",
      value: closeRate,
      set: setCloseRate,
      min: 5,
      max: 50,
      step: 1,
      display: `${closeRate}%`,
      icon: Target,
    },
    {
      label: "Average contract value",
      value: acv,
      set: setAcv,
      min: 2000,
      max: 120000,
      step: 1000,
      display: fmt(acv),
      icon: DollarSign,
    },
  ];

  return (
    <section data-testid="roi-estimator" className="relative overflow-hidden py-24 md:py-28">
      <GlowOrb className="right-[-6%] top-1/3 h-[460px] w-[460px] opacity-70" />
      <Container>
        <div className="max-w-3xl">
          <Eyebrow>Pipeline Estimator</Eyebrow>
          <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
            Model what qualified meetings are worth
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
            Drag the sliders to project the deals and revenue Appointment Lab could drive for your
            business.
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-[1fr_1fr] items-stretch">
          {/* Sliders */}
          <Reveal className="rounded-3xl border border-white/10 bg-navy-800 p-8 md:p-10">
            <div className="space-y-10">
              {controls.map((c) => (
                <div key={c.label} data-testid={`estimator-control-${c.label.split(" ")[0].toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2.5 text-sm text-white/70">
                      <c.icon className="h-4 w-4 text-gold" />
                      {c.label}
                    </span>
                    <span className="font-mono-jb text-xl font-semibold text-gold">
                      {c.display}
                    </span>
                  </div>
                  <Slider
                    className="mt-5"
                    value={[c.value]}
                    min={c.min}
                    max={c.max}
                    step={c.step}
                    onValueChange={(v) => c.set(v[0])}
                    aria-label={c.label}
                  />
                  <div className="mt-2 flex justify-between font-mono-jb text-[11px] text-white/35">
                    <span>{c.label.includes("value") ? fmt(c.min) : c.min}</span>
                    <span>{c.label.includes("value") ? fmt(c.max) : c.max}</span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Results */}
          <Reveal
            delay={0.1}
            className="relative overflow-hidden rounded-3xl border border-gold/25 bg-navy-800 p-8 md:p-10 shadow-[0_0_60px_rgba(255,215,0,0.08)]"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 gold-radial" />
            <p className="font-mono-jb text-xs uppercase tracking-[0.24em] text-white/45">
              Projected monthly outcome
            </p>

            <div className="mt-6 grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-white/50">New deals / mo</p>
                <motion.p
                  key={deals}
                  initial={{ opacity: 0.4, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-1 font-mono-jb text-4xl md:text-5xl font-semibold text-white"
                >
                  {deals.toFixed(1)}
                </motion.p>
              </div>
              <div>
                <p className="text-sm text-white/50">New revenue / mo</p>
                <motion.p
                  key={monthlyRev}
                  initial={{ opacity: 0.4, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-1 font-mono-jb text-4xl md:text-5xl font-semibold text-gold text-glow-gold"
                >
                  {fmt(monthlyRev)}
                </motion.p>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-white/10 bg-navy-900 p-6">
              <div className="flex items-center gap-2 text-sm text-white/60">
                <TrendingUp className="h-4 w-4 text-gold" />
                Annualized projection
              </div>
              <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="font-mono-jb text-3xl font-semibold text-white">
                    {Math.round(annualDeals)}
                  </p>
                  <p className="text-xs text-white/50">deals / year</p>
                </div>
                <div className="text-right">
                  <p className="font-mono-jb text-3xl font-semibold text-gold">
                    {fmtFull(annualRev)}
                  </p>
                  <p className="text-xs text-white/50">new ARR influenced</p>
                </div>
              </div>
            </div>

            <p className="mt-5 text-xs text-white/40 leading-relaxed">
              Estimates are illustrative and based on your inputs — not a guarantee. Your real
              numbers depend on offer, ICP fit and sales execution.
            </p>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};
